import { MAX_REASON_NUTRIENTS, MAX_VISIBLE_ADVICE } from './config';
import { nutrientMeta } from './nutrients';
import { joinEs, num } from './text';
import type { CloseDayResult, Deficit } from './close-day';
import type { HistoryResult } from './history';
import type { Advice, NutrientKey } from './types';

/**
 * Selección final de avisos (SPEC 7).
 *
 * Nunca más de 3. Un panel con ocho avisos no se lee: se ignora entero, y
 * entonces también se pierden los dos que importaban.
 *
 * El orden es por prioridad y, a igualdad, el orden de llegada. Es estable a
 * propósito: que los avisos bailen de sitio entre recargas hace que parezcan
 * poco fiables.
 */
export function topAdvice(all: readonly Advice[]): Advice[] {
  return [...all]
    .map((advice, index) => ({ advice, index }))
    .sort((a, b) => a.advice.priority - b.advice.priority || a.index - b.index)
    .slice(0, MAX_VISIBLE_ADVICE)
    .map((entry) => entry.advice);
}

function unitOf(key: NutrientKey): string {
  const unit = nutrientMeta(key).unit;
  return unit === 'ug' ? 'µg' : unit;
}

function amount(key: NutrientKey, value: number): string {
  return `${num(value, nutrientMeta(key).decimals)} ${unitOf(key)}`;
}

/**
 * Convierte una propuesta de cierre en un aviso legible.
 *
 * El mensaje dice QUÉ comer y el motivo dice QUÉ tapa, con cifras. Un consejo
 * sin el porqué no se sigue: parece una orden arbitraria.
 */
export function closeDayAdvice(
  result: CloseDayResult,
  deficits: readonly Deficit[],
  options: { hideCalories?: boolean } = {},
): Advice | null {
  if (result.items.length === 0) return null;

  const foods = result.items.map(
    (item) => `${num(item.grams)} g de ${item.candidate.name.toLowerCase()}`,
  );

  // Solo los huecos más grandes. Enumerar diecisiete nutrientes no informa:
  // es un muro de texto que nadie lee.
  const covered = [...deficits]
    .sort((a, b) => b.weight - a.weight)
    .slice(0, MAX_REASON_NUTRIENTS)
    .map((deficit) => {
      const provided = result.items.reduce(
        (sum, item) => sum + ((item.candidate.per100g[deficit.key] ?? 0) * item.grams) / 100,
        0,
      );
      if (provided <= 0) return null;
      const label = nutrientMeta(deficit.key).label.toLowerCase();
      // Cuando el aporte supera al hueco, decir "182 g de los 130 g que faltan"
      // confunde: se dice que lo completa y cuánto faltaba, sin comparar.
      return provided >= deficit.missing
        ? `completa ${label} (te faltaban ${amount(deficit.key, deficit.missing)})`
        : `acerca ${label} (${amount(deficit.key, provided)} de los ${amount(deficit.key, deficit.missing)} que faltan)`;
    })
    .filter((text): text is string => text != null);

  const tail = result.partial
    ? ` No llega a cubrirlo todo con ${options.hideCalories ? 'el margen' : 'las calorías'} que te ${options.hideCalories ? 'queda' : 'quedan'}, pero es lo que más se acerca.`
    : '';

  // Quien oculta las calorías no quiere verlas tampoco aquí: SPEC 9 pide el
  // modo para dejar de contar, y un aviso que las canta lo rompe.
  const kcal = options.hideCalories ? '' : ` (${num(result.totalKcal)} kcal)`;

  return {
    type: 'cierre_dieta',
    priority: 1,
    message: `Para cerrar el día: ${joinEs(foods)}${kcal}.${tail}`,
    reason: covered.length > 0 ? `Así ${joinEs(covered)}.` : 'Ajusta lo que falta del día.',
  };
}

/**
 * Aviso de tendencia histórica de un nutriente (SPEC 7.5).
 *
 * Solo habla cuando el estado es «alerta». «Silencio», «vigilar» y «datos
 * insuficientes» no generan aviso: el primero porque ya está corregido, el
 * segundo porque solo se ve en el detalle y el tercero porque no saber no es
 * lo mismo que faltar.
 */
export function historyAdvice(key: NutrientKey, result: HistoryResult): Advice | null {
  if (result.status !== 'alerta') return null;

  const label = nutrientMeta(key).label.toLowerCase();
  return {
    type: 'historico',
    priority: 2,
    message: `Llevas tiempo por debajo de tu objetivo de ${label}.`,
    reason: `Últimos 7 días válidos: ${num(result.last7 * 100)} % del objetivo. Media ponderada del último mes y medio: ${num(result.ema * 100)} %. Con ${num(result.validDays)} días registrados por completo.`,
  };
}
